// La ventana de la Sala de Control: misma superficie que el chat (flota a la derecha del equipo, con el
// mundo atenuado detrás). Cabecera con la frescura de los datos; hoy solo el pulso.
import { Activity, Brain, Cpu, DollarSign, Hammer, Radio, Receipt, Search, ShieldCheck, Users, Wrench, X, type LucideIcon } from 'lucide-react';
import { useEffect } from 'react';
import { t, type TKey } from '../../../i18n';
import { cn } from '../../../lib/utils';
import { Aviso } from '../ui';
import { edad, SIN_VALOR, ZONA_SALA } from './formato';
import { Pulso } from './Pulso';
import { RINCONES } from './Rincones';
import type { NombreFotoSala, NombreRinconSala, PulsoSala } from './tipos';
import { despiertaRincones } from './useSalaRincon';

export type PestanaSala = 'pulso' | NombreRinconSala;
export const PESTANAS: PestanaSala[] = ['pulso', 'cerebros', 'dinero', 'gasto', 'fans', 'seguridad', 'maquina', 'cuentas', 'obra', 'taller'];
/** Rebrand 03-11 (ADR D-037, maqueta web/qa/rebrand-apple/despues/9-sala-pulso.png): en el escritorio las pestañas son una
 *  barra lateral por bloques con iconos de color, como los Ajustes del Mac. Mismo orden y mismas pestañas. */
const BLOQUES: Array<{ id: 'negocio' | 'calypso' | 'coste' | 'obras'; pestanas: PestanaSala[] }> = [
  { id: 'negocio', pestanas: ['pulso', 'dinero', 'fans'] },
  { id: 'calypso', pestanas: ['cerebros', 'seguridad', 'maquina', 'cuentas'] },
  { id: 'coste', pestanas: ['gasto'] },
  { id: 'obras', pestanas: ['obra', 'taller'] },
];
const ICONO: Record<PestanaSala, { icono: LucideIcon; fondo: string }> = {
  pulso: { icono: Activity, fondo: 'bg-oficina-error' },
  dinero: { icono: DollarSign, fondo: 'bg-oficina-trabajando' },
  fans: { icono: Users, fondo: 'bg-oficina-bloqueado' },
  cerebros: { icono: Brain, fondo: 'bg-oficina-cerrando' },
  seguridad: { icono: ShieldCheck, fondo: 'bg-oficina-cerebro-azul' },
  maquina: { icono: Cpu, fondo: 'bg-oficina-esperando' },
  cuentas: { icono: Search, fondo: 'bg-oficina-serie-3' },
  gasto: { icono: Receipt, fondo: 'bg-oficina-acento' },
  obra: { icono: Hammer, fondo: 'bg-oficina-bloqueado' },
  taller: { icono: Wrench, fondo: 'bg-oficina-esperando' },
};

/** Lo que el servidor calcula solo cuando alguien lo mira (el gasto va por su propia ruta con fechas); «modelos» es la
 *  vista por modelo del Pulso. */
const RINCONES_A_DESPERTAR: NombreFotoSala[] = ['cerebros', 'dinero', 'fans', 'seguridad', 'maquina', 'cuentas', 'obra', 'taller', 'subidas', 'modelos'];

export function VentanaSala({
  pulso,
  fallo,
  pestana = 'pulso',
  alCambiarPestana,
  alCerrar,
  movil = false,
}: {
  pulso: PulsoSala | null;
  fallo: boolean;
  pestana?: PestanaSala;
  alCambiarPestana?: (p: PestanaSala) => void;
  alCerrar: () => void;
  /** A pantalla completa en el móvil (SalaMovil): sin borde ni esquinas, cabecera corta y cierre de 44 px. */
  movil?: boolean;
}) {
  // 22-10 (revisión de la sala): al abrirla se despiertan todos los rincones; si no, cada pestaña empezaba a calcularse
  // al entrar y tardaba hasta un minuto. El servidor solo trabaja mientras alguien mira: abrir la sala es mirar.
  useEffect(() => {
    despiertaRincones(RINCONES_A_DESPERTAR);
  }, []);
  // 22-10 (revisión de la sala): Escape cierra la sala, como el chat. Lo que ya atendió la tecla (el calendario, la
  // etiqueta de una gráfica) la marca y no cierra nada más; con un diálogo encima, la tecla es suya.
  useEffect(() => {
    const tecla = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !e.defaultPrevented && !document.querySelector('[role="dialog"]')) alCerrar();
    };
    window.addEventListener('keydown', tecla);
    return () => window.removeEventListener('keydown', tecla);
  }, [alCerrar]);
  const estado = pulso?.estado ?? 'sin_datos';
  const hora = pulso?.generado_at
    ? `${new Date(pulso.generado_at).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit', timeZone: ZONA_SALA })} UTC`
    : SIN_VALOR;
  const fr = pulso?.frescura;
  // Las conversaciones de Calypso alimentan los avisos (velocidad); si llegan tarde, se atenúan.
  const conversacionesTarde = fr?.conversaciones == null || fr.conversaciones > 15 * 60;
  return (
    <section
      aria-label={t('salaControl.titulo')}
      className={cn(
        // Como Salud: el contenido sobre el fondo oscuro para que las tarjetas (gris de ventana) destaquen.
        'flex h-full overflow-hidden bg-oficina-fondo text-oficina-texto',
        movil ? 'flex-col' : 'flex-row rounded-o-xl shadow-o-hoja',
      )}
    >
      {movil ? null : (
        <nav className="flex w-52 shrink-0 flex-col overflow-y-auto border-r border-oficina-linea bg-oficina-panel px-2.5 pb-3 pt-4" role="tablist" aria-orientation="vertical" aria-label={t('salaControl.titulo')}>
          <p className="flex items-center gap-2 px-2 pb-2 text-o-cuerpo font-semibold">
            <Radio className="h-5 w-5 shrink-0 text-oficina-marca" aria-hidden />
            {t('salaControl.titulo')}
          </p>
          {BLOQUES.map((b) => (
            <div key={b.id} className="pt-2">
              <p className="px-2 pb-1 text-o-nota font-semibold text-oficina-texto3">{t(`salaControl.grupos.${b.id}`)}</p>
              {b.pestanas.map((p) => {
                const { icono: Icono, fondo } = ICONO[p];
                return (
                  <button
                    key={p}
                    type="button"
                    role="tab"
                    aria-selected={p === pestana}
                    onClick={() => alCambiarPestana?.(p)}
                    className={cn(
                      'flex w-full items-center gap-2.5 rounded-o-s px-2 py-1.5 text-left text-o-texto outline-none transition-colors duration-150 focus-visible:ring-2 focus-visible:ring-oficina-enlace/70',
                      p === pestana ? 'bg-oficina-pulsado font-semibold text-oficina-texto' : 'text-oficina-texto hover:bg-white/[0.05]',
                    )}
                  >
                    <span className={cn('flex h-6 w-6 shrink-0 items-center justify-center rounded-o-s text-white', fondo)} aria-hidden>
                      <Icono className="h-3.5 w-3.5" strokeWidth={2.4} />
                    </span>
                    {t(`salaControl.pestanas.${p}`)}
                  </button>
                );
              })}
            </div>
          ))}
        </nav>
      )}
      <div className="flex min-w-0 flex-1 flex-col overflow-hidden">
      <header className={cn('flex items-center gap-3', movil ? 'px-3 pb-2 pt-2' : 'px-5 pb-3 pt-4')}>
        {movil ? <Radio className="h-6 w-6 shrink-0 text-oficina-marca" aria-hidden /> : null}
        <div className="min-w-0">
          {/* En el escritorio el título grande es la pestaña (como Salud o Ajustes); la sala ya se nombra en la barra. */}
          <h2 className={cn('font-bold', movil ? 'text-o-titulo2' : 'text-o-titulo')}>{movil ? t('salaControl.titulo') : t(`salaControl.pestanas.${pestana}`)}</h2>
          {movil ? null : <p className="text-o-sub text-oficina-texto2">{t('salaControl.subtitulo')}</p>}
        </div>
        <span className={cn('flex shrink-0 items-center gap-1.5 rounded-full bg-oficina-elevada px-2.5 py-1 text-o-nota text-oficina-texto2', movil ? 'ml-1' : 'ml-4')}>
          <span
            className={cn(
              'h-2 w-2 rounded-full',
              estado === 'ok' ? 'bg-oficina-trabajando' : estado === 'retraso' ? 'bg-oficina-error' : 'bg-oficina-esperando',
            )}
          />
          {estado === 'ok' ? t('salaControl.alDia', { hora }) : estado === 'retraso' ? t('salaControl.conRetraso') : t('salaControl.sinDatos')}
        </span>
        <button
          type="button"
          onClick={alCerrar}
          aria-label={t('salaControl.cerrar')}
          className={cn(
            'ml-auto inline-flex shrink-0 items-center justify-center rounded-full bg-oficina-control text-oficina-texto2 transition-colors hover:bg-oficina-pulsado hover:text-oficina-texto',
            movil ? 'h-11 w-11' : 'h-8 w-8',
          )}
        >
          <X className="h-[18px] w-[18px]" aria-hidden />
        </button>
      </header>
      {movil ? (
      <nav className="flex gap-1 overflow-x-auto border-b border-oficina-linea px-4" role="tablist" aria-label={t('salaControl.titulo')}>
        {PESTANAS.map((p) => (
          <button
            key={p}
            type="button"
            role="tab"
            aria-selected={p === pestana}
            onClick={() => alCambiarPestana?.(p)}
            className={cn(
              'shrink-0 whitespace-nowrap border-b-2 px-3 pb-3 pt-2 text-o-cuerpo font-medium transition-colors outline-none focus-visible:text-oficina-texto',
              p === pestana ? 'border-oficina-acento text-oficina-texto' : 'border-transparent text-oficina-texto2 hover:text-oficina-texto',
            )}
          >
            {t(`salaControl.pestanas.${p}`)}
          </button>
        ))}
      </nav>
      ) : null}

      <div className={cn('flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto', movil ? 'p-3' : 'p-5')}>
        {pestana === 'pulso' && fallo && !pulso ? <Aviso tono="error">{t('salaControl.errorCarga')}</Aviso> : null}
        {pestana === 'pulso' && pulso?.error ? (
          <Aviso tono="aviso">{t('salaControl.rincones.desfasado', { edad: edad(pulso.edad_s ?? 0), motivo: t(`salaControl.rincones.errores.${pulso.error.mensaje}` as TKey) })}</Aviso>
        ) : pestana === 'pulso' && estado === 'retraso' && pulso ? (
          <Aviso tono="aviso">{t('salaControl.retrasoAviso')}</Aviso>
        ) : null}
        {pestana !== 'pulso' ? (
          RINCONES[pestana]()
        ) : pulso && estado !== 'sin_datos' ? (
          <Pulso pulso={pulso} atenuarAvisos={conversacionesTarde} />
        ) : !fallo ? (
          <p className="py-10 text-center text-o-texto text-oficina-texto3">{pulso ? t('salaControl.sinDatos') : t('salaControl.cargando')}</p>
        ) : null}
      </div>

      {/* «Gasto» lleva su propio pie (cuándo se actualizó y hasta qué hora hay gasto registrado). */}
      {fr && pestana !== 'gasto' ? (
        <footer className="flex flex-wrap gap-x-5 gap-y-1 border-t border-oficina-linea px-5 py-2.5 text-o-nota text-oficina-texto3">
          {fr.crm !== null ? <span>{t('salaControl.fuentes.crm', { edad: edad(fr.crm) })}</span> : null}
          {fr.conversaciones !== null ? (
            <span className={cn(conversacionesTarde && 'text-oficina-error-claro')}>
              {t('salaControl.fuentes.conversaciones', { edad: edad(fr.conversaciones) })}
            </span>
          ) : null}
        </footer>
      ) : null}
      </div>
    </section>
  );
}
